/**
 * utils/pagination.js
 * Pure pagination for a transaction's line list across a multi-page printed document
 * (PRD §5.2 — Invoice Pusat's running-subtotal pagination; Invoice Cabang's own
 * total-at-end pagination, T2d, can reuse this same engine by reading only `pages`/
 * `pageCount` and ignoring `carryIn`/`pageTotal` per page — cabang shows a total only on
 * the final page, not a running one). No DOM, no React, deterministic given its inputs.
 *
 * Reuses `goodsTotal` from txnStatus.js for every sum (per-page and running) so this engine's
 * arithmetic can never drift from the goods-only qty*unitPrice math used everywhere else in the
 * app — one formula, not two independently-maintained ones.
 *
 * **Cost items Brief 2**: deliberately depends on `goodsTotal`, NOT the app-wide `txnTotal` —
 * `txnTotal` now also folds in "tagihan"/"tagihanSupplier"-type costItems (see txnStatus.js's
 * own header comment), but a printed document's TOTAL row must never reflect them in this pass
 * (a hard, confirmed non-goal of that brief — "No printing changes"). Depending on `goodsTotal`
 * makes that structural, not incidental: `goodsTotal` has no `costItems`-reading code path at
 * all, so this module can never start including cost items in a printed total even if a future
 * call site below were changed to pass a full `txn` object instead of a bare `{lines}` one.
 */
import { goodsTotal } from "./txnStatus";

/**
 * The one correct character for a "filler" (empty) print-table cell (PRD §5.1's fill-the-page
 * rule). MUST be a genuine non-breaking space (U+00A0), never an ordinary space (U+0020) — a
 * plain space is CSS-collapsible and, as the cell's only content, collapses away entirely under
 * `white-space: normal`, producing a visibly SHORTER row than a real content row (confirmed via
 * a real Chromium print-media render, Phase T2c-2-fix-2: an ordinary-space filler cell measured
 * 7px tall vs. a real row's 26.98px). Exported here — the one module every print document
 * already imports for pagination — specifically so this exact character is never re-typed (and
 * silently gotten wrong again, as happened to `SuratJalanPusat.js`/`Checklist.js` in T2c-2)
 * across the growing family of print documents; every filler `<td>` must render `{NBSP}`.
 *
 * Deliberately written as the ` ` escape, not a literal character between the quotes —
 * a literal non-breaking space is visually INDISTINGUISHABLE from an ordinary one in an editor
 * (the exact root cause of the T2c-2-fix-2 bug this comment already describes) and, worse, is
 * NOT even safe against this codebase's own tooling: a routine file rewrite during this
 * session's own work silently downgraded a literal U+00A0 back to a plain U+0020, caught only
 * by explicitly checking the file's raw bytes afterward. The explicit escape can't suffer either
 * failure mode — it's unambiguous in source and immune to any editor/tool that normalizes
 * "invisible" whitespace.
 */
export const NBSP = " ";

/**
 * Screen-only "before you print" reminders shown in each document's `.print-doc-overlay__actions`
 * bar, right next to the Cetak button (never printed — `@media print` already hides the whole
 * actions bar). Exported here for the SAME reason `NBSP` is — this is the one module every print
 * document already imports, so the reminder text can't silently diverge across the four
 * documents that share the `cabang`-page wording, the exact class of bug T2c-2-fix-2 already hit
 * once for a filler character re-typed per document instead of shared from one place.
 *
 * Originally existed because Chrome's print dialog remembers only ONE paper-size/margin
 * configuration per origin, and pusat/cabang used to need DIFFERENT paper sizes (Letter/Letter
 * Fanfold vs Statement) — whichever was picked last for one document family would silently
 * carry over to the other, reproducing a phantom-extra-page overflow. **The cabang @page
 * geometry fix (printFoundation.css's own header comment, last dated paragraph) removed that
 * divergence** — `@page cabang` is now byte-identical to `@page pusat`, so all five documents
 * target the SAME paper size/margin setting; there is no longer a cross-family mismatch to
 * silently carry over. The two constants are kept as a general "confirm your settings before
 * printing" nudge (harmless, still worth keeping — Chrome's dialog can still default to
 * something unrelated if the user printed something else entirely in between) and, not
 * incidentally, now read identical text — see each one's own comment for why.
 */
export const PRINT_SETTINGS_REMINDER_PUSAT =
  "Sebelum mencetak: Paper size → Letter (atau Letter Fanfold), Margins → None";
// Was "Paper size → Statement, Margins → None" — the cabang @page geometry fix above made
// @page cabang byte-identical to @page pusat, so this reminder now names the SAME paper size
// PRINT_SETTINGS_REMINDER_PUSAT does; kept as a separate constant (not collapsed into one)
// since the four cabang documents already import it under this name and a future divergence
// (if the two page types are ever tuned differently again) shouldn't be assumed impossible.
export const PRINT_SETTINGS_REMINDER_CABANG =
  "Sebelum mencetak: Paper size → Letter (atau Letter Fanfold), Margins → None";

/**
 * @param {Array} lines - transaction lines ({ qty, unitPrice, ... })
 * @param {number} rowsPerPage - named constant per template, caller-supplied (never hardcoded here)
 * @returns {{
 *   pages: Array<{ pageNumber: number, lines: Array, carryIn: number, pageTotal: number }>,
 *   pageCount: number,
 *   grandTotal: number
 * }}
 *   `carryIn` is the cumulative total carried in FROM the previous page (0 on page 1).
 *   `pageTotal` is CUMULATIVE through this page (carryIn + this page's own lines) — so the
 *   last page's `pageTotal` always equals `grandTotal`, which always equals `goodsTotal({lines})`
 *   — deliberately goods-only, see the file header comment.
 */
export function paginateLines(lines, rowsPerPage) {
  const safeLines = lines || [];
  const pageCount = Math.max(1, Math.ceil(safeLines.length / rowsPerPage));
  const pages = [];
  let carry = 0;
  for (let i = 0; i < pageCount; i++) {
    const slice = safeLines.slice(i * rowsPerPage, (i + 1) * rowsPerPage);
    const ownTotal = goodsTotal({ lines: slice });
    const carryIn = carry;
    const pageTotal = carryIn + ownTotal;
    pages.push({ pageNumber: i + 1, lines: slice, carryIn, pageTotal });
    carry = pageTotal;
  }
  return { pages, pageCount, grandTotal: carry };
}
